import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import api from "../services/api.ts";

interface Role {
  id: string;
  created_at: string;
  updated_at: string;
  org_id: string | null;
  name: string;
  desc: string;
  privileges: string[];
}

interface Partner {
  id: string;
  name: string;
  email: string;
  status: string;
  partnerNumber: string;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  partnerNumber: string;
  userType: string;
  role: Role;
  partner: Partner;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (
    partnerNumber: string,
    email: string,
    password: string
  ) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 自动从 localStorage 恢复用户
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (
    partnerNumber: string,
    email: string,
    password: string
  ) => {
    console.log("[AuthContext] login params", {
      partnerNumber,
      email,
      password,
    });
    try {
      const response = await api.post("/partner/auth/login", {
        partnerNumber,
        email,
        password,
      });
      console.log("[AuthContext] login response", response);
      if (response.data && response.data.error) {
        console.log(
          "[AuthContext] login error from backend",
          response.data.error
        );
        throw new Error(response.data.error);
      }
      const {
        userId,
        firstName,
        lastName,
        email: userEmail,
        jwtToken,
        refreshToken,
        role,
        partner,
        userType,
      } = response.data;
      const userObj: User = {
        id: userId,
        email: userEmail,
        firstName: firstName || "",
        lastName: lastName || "",
        partnerNumber: partner?.partnerNumber || "",
        userType,
        role,
        partner,
      };
      localStorage.setItem("token", jwtToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("user", JSON.stringify(userObj));
      setUser(userObj);
    } catch (err) {
      console.log("[AuthContext] login exception", err);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    setUser(null);
    // 跳转由调用方处理
  };

  const updateUser = (updatedUser: User) => {
    localStorage.setItem("user", JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
